import { useMutation, useQuery } from '@tanstack/react-query';

import competencyTranslationService from './competencyTranslationService';

import type {
    CompetencyTranslationChangeRequest,
    CompetencyTranslationGroup,
    CompetencyTranslationGroupSummary,
} from '@/types/competencies/CompetencyTranslation';

import { queryClient } from '@/AppWithProviders';
import {
    CompetenciesQueryKey,
    CompetencyQueryKey,
    CompetencyQuestionQueryKey,
} from '@/constants/QueryKeys';

export const invalidateCompetencyTranslationQueries = () => {
    queryClient.invalidateQueries({
        queryKey: [CompetenciesQueryKey.COMPETENCY_TRANSLATION_GROUP_SUMMARIES],
    });
    queryClient.invalidateQueries({
        queryKey: [CompetenciesQueryKey.COMPETENCY_TRANSLATION_GROUP],
    });
    queryClient.invalidateQueries({
        queryKey: [CompetenciesQueryKey.COMPETENCY_TRANSLATIONS],
    });
};

export const useGetCompetencyTranslations = () => {
    return useQuery<CompetencyTranslationGroup[]>({
        queryKey: [CompetenciesQueryKey.COMPETENCY_TRANSLATIONS],
        queryFn: competencyTranslationService.getCompetencyTranslations,
    });
};

export const useGetCompetencyTranslationGroupSummaries = () => {
    return useQuery<CompetencyTranslationGroupSummary[]>({
        queryKey: [CompetenciesQueryKey.COMPETENCY_TRANSLATION_GROUP_SUMMARIES],
        queryFn: competencyTranslationService.getGroupSummaries,
    });
};

export const useGetCompetencyTranslationGroup = (groupId: string | null) => {
    return useQuery<CompetencyTranslationGroup>({
        queryKey: [CompetenciesQueryKey.COMPETENCY_TRANSLATION_GROUP, groupId],
        queryFn: () => competencyTranslationService.getGroupById(groupId!),
        enabled: groupId !== null,
    });
};

export const useUpdateCompetencyTranslations = () => {
    return useMutation<CompetencyTranslationGroup[], Error, CompetencyTranslationChangeRequest[]>({
        mutationFn: (changes) => competencyTranslationService.updateCompetencyTranslations(changes),
        onSuccess: (data) => {
            queryClient.setQueryData([CompetenciesQueryKey.COMPETENCY_TRANSLATIONS], data);
            invalidateCompetencyTranslationQueries();

            // The competency and question edit forms merge the edited language into the
            // dictionaries they read from these caches, then PUT the whole dictionary back.
            // Without this invalidation they keep pre-translation data (the groups query stays
            // mounted across tabs and never refetches on its own), so the next edit in one
            // language silently wipes the translations saved here.
            queryClient.invalidateQueries({
                queryKey: [CompetencyQueryKey.COMPETENCY_GROUPS],
            });
            queryClient.invalidateQueries({
                queryKey: [CompetencyQuestionQueryKey.COMPETENCY_QUESTIONS],
            });
        },
    });
};
